import { useCallback } from 'react'

import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form'

import {
  Member,
  MemberListFilterByOperator,
} from '@tribeplatform/gql-client/types'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MemberGroups } from '../../../CMS/types.js'
import { MemberPicker } from '../../../EntityPicker/MemberPicker.js'
import { useI18n } from '../../../i18n/providers/I18nProvider.js'
import { NEW_CHAT_MEMBERS_COUNT_LIMIT } from '../../constants.js'

interface Props {
  canCreateGroup: boolean
  reachableMemberGroup: MemberGroups
}

export const NewChatMemberPicker = ({
  canCreateGroup,
  reachableMemberGroup,
}: Props) => {
  const { control } = useFormContext()
  const { $t } = useI18n()

  const onMemberChange = useCallback(
    (field: ControllerRenderProps, value: Member[] | Member) => {
      const isArray = Array.isArray(value)
      const limitExceeded =
        isArray && value?.length > NEW_CHAT_MEMBERS_COUNT_LIMIT

      if (!limitExceeded) {
        field.onChange(value)
        return
      }

      toast({
        title: $t(
          {
            defaultMessage:
              'You can select up to {max} members initially. You may add more people after creating the group.',
            id: 'Messaging.NewGroupChat.MembersField.Description',
          },
          {
            max: NEW_CHAT_MEMBERS_COUNT_LIMIT,
          },
        ),
        status: 'warning',
      })
    },
    [$t],
  )

  return (
    <Controller
      name="members"
      control={control}
      render={({ field }) => (
        <MemberPicker
          {...field}
          noSuggestions
          multiple={canCreateGroup}
          value={field.value}
          onChange={value => onMemberChange(field, value)}
          placeholder={$t({
            defaultMessage: 'Search for members',
            id: 'Messaging.SearchMembers',
          })}
          className="w-full"
          memberGroup={reachableMemberGroup}
          variables={query => ({
            filterBy: [
              {
                key: 'name',
                operator: MemberListFilterByOperator.contains,
                value: JSON.stringify(query),
              },
            ],
          })}
          emptyMessage={$t({
            defaultMessage: 'No {MEMBERS} found with that name',
            id: 'Messaging.RecipientNotFound',
          })}
        />
      )}
    />
  )
}
